import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LanguageSelector } from '@components/organisms/language-selector';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import type { DocsVerificationLivenessTagErrorEnum } from '@models/enums';
import { DocsVerificationTabEnum, DocsVerificationTypeEnum } from '@models/enums';
import type { FaceDetectResult, FileWithPreview, ThemeProps } from '@models/types';
import { RequirementTypeEnum } from '@modules/dashboard/pages/docs-verification/docs-verification-page/requirements/types';
import type { BiometricsProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/selfie/types';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import type { RootState } from '@store/root-reducer';
import type { VerificationFileState } from '@store/slices/docs-verification/docs-verification-slice';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { scrollTop } from '@utils/util';
import cn from 'classnames';

import { Timer } from '../../docs-verification-steps/start/timer';
import { usePageTitle } from '../../hooks/use-page-title';
import { useUploadFile } from '../../hooks/use-upload-file';
import { MobileHeader } from '../../mobile-header';
import { Requirements } from '../../requirements';
import { STEPS_MOBILE_TITLE } from '../constants';

import { Liveness } from './liveness';
import { SelfieContainer } from './styles';

export const Selfie: React.FC<BiometricsProps> = ({ onSubmit }) => {
    const dispatch = useDispatch();
    const theme = useTheme<ThemeProps>();
    const { isCameraMode, isShowRequirements } = useSelector(
        (state: RootState) => state.docsVerification,
    );
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const { uploadFile } = useUploadFile();
    const { height } = useScreenFullHeight();
    const { pageTitle } = usePageTitle();

    const handleFilesChange = useCallback(
        async (
            file: FileWithPreview,
            tag?: DocsVerificationLivenessTagErrorEnum,
            faceDetectResult?: FaceDetectResult,
        ) => {
            dispatch(
                docsVerificationSlice.actions.deleteFilesByTab(DocsVerificationTabEnum.BIOMETRICS),
            );

            const response = await uploadFile({
                file,
                documentType: DocsVerificationTypeEnum.LIVENESS,
                tab: DocsVerificationTabEnum.BIOMETRICS,
                faceDetectResult,
            });

            // small delay to prevent screen blink
            return new Promise<VerificationFileState>((resolve) => {
                setTimeout(() => {
                    resolve(response);
                }, 100);
            });
        },
        [uploadFile, dispatch],
    );

    const handleBackClick = () => {
        if (isCameraMode) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(false));
        }

        if (isShowRequirements) {
            dispatch(docsVerificationSlice.actions.setIsShowRequirements(false));
        } else {
            dispatch(docsVerificationSlice.actions.goBackStep());
        }

        setTimeout(() => {
            scrollTop();
        });
    };

    const stepsMobileTitleNode = window.document.getElementById(STEPS_MOBILE_TITLE);

    return (
        <SelfieContainer
            style={{
                minHeight:
                    matchesMobileSm && height
                        ? height -
                          32 -
                          (stepsMobileTitleNode?.clientHeight
                              ? stepsMobileTitleNode.clientHeight
                              : 0)
                        : undefined,
            }}
        >
            {matchesMobileSm &&
                !isShowRequirements &&
                stepsMobileTitleNode &&
                createPortal(
                    <React.Fragment>
                        <MobileHeader
                            onBack={handleBackClick}
                            title={pageTitle}
                            className={cn({ 'selfie-fixed-header': isCameraMode })}
                            isFixed={isCameraMode}
                        />
                        {!isCameraMode && <Timer isHidden={true} />}
                    </React.Fragment>,
                    stepsMobileTitleNode,
                )}

            {isShowRequirements ? (
                <Requirements type={RequirementTypeEnum.SELFIE} onBack={handleBackClick} />
            ) : (
                <React.Fragment>
                    {!matchesMobileSm && (
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                maxWidth: '632px',
                                justifyContent: 'flex-end',
                                margin: 'auto',
                            }}
                        >
                            <LanguageSelector className='language-selector' />
                        </Box>
                    )}
                    <div className={cn('main', 'main-liveness')}>
                        <Liveness onFileChange={handleFilesChange} onSubmit={onSubmit} />
                    </div>
                </React.Fragment>
            )}
        </SelfieContainer>
    );
};
